import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useState } from 'react'
import Header from './header/Header.jsx'
import FavouritesBar from './header/FavouritesBar.jsx'
import Sidebar from './sidebar/Sidebar.jsx'
import SiteRoutes from '../../routes.js'
import AmbuNetLogo from '../../assets/images/ambunetLogo.png'
import API, { setGlobalApiErrorHandler } from '../../services/API.js'
import { Route, Routes, useLocation } from 'react-router-dom'
//@MUI
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import SidebarEPCR from '../../pages/patientRecords/ePCR/SidebarEPCR.jsx'
import EPCR from '../../pages/patientRecords/ePCR/EPCR.jsx'
import Typography from '@mui/material/Typography'
import EventControlPage from '../../pages/command/ControlRoom/ControlPage/ControlPage.jsx'
import BillingWarning from './header/BillingWarning.jsx'
import AgreementReminder from './header/AgreementReminder.jsx'
import PortalRequestsBanner from './header/PortalRequestsBanner.jsx'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../App.js'
import TenantOnboarding from '../../pages/admin/siteConfiguration/Setup/Setup.jsx'
import DocsLayout from '../../docs/components/DocsLayout.jsx'
import { Stack } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import { useNavigate } from 'react-router-dom'
import { Button, Paper } from '@mui/material'
import NotAuthorised from '../NotAuthorised.jsx'
import PTSControllerOverview from '../../pages/patientTransport/Control/PTSControl.jsx'
import PTSCrewView from '../../pages/patientTransport/CrewView/CrewView.jsx'
import { PTSUpgrade } from '../../routes/PatientTransportRoutes.js'
import PublicDocs from '../../docs/components/PublicDocs.jsx'
import { docRoutes } from '../../docs/routes/_config.js'
import ConnectionStatusBanner from '../Connection/ConnectionStatusBanner.jsx'
import { TreatmentCentre } from '../../pages/command/TreatmentCentres/TreatmentCentre.jsx'
import UserSessionIdleTimer from '../../partials/UserSessionIdleTimer.jsx'
import UpdatesHub from '../../pages/dev/Updates.jsx'
import { NavCountsProvider } from './NavCountsContext.jsx'
import { RecentPagesTracker } from '../../navigation/recents.js'

export const SidebarContext = createContext({})
export const SiteModeContext = createContext({})

const MainFrame = () => {
  const { user, setUser } = useContext(UserContext)
  const [sidebarState, setSidebarState] = useState(() => ({
    collapsed: !!user?.uiPreferences?.sidebarCollapsed,
    mobileOpen: false,
  }))

  // Persist the collapsed/expanded choice (debounced - the header button can
  // be clicked in bursts). Only the header button goes through here: pages
  // that force the sidebar closed while mounted (docs, site configuration)
  // call setSidebarState directly, and those transient states must never be
  // saved as the user's preference.
  const collapseSaveTimer = useRef(null)
  const toggleSidebarCollapsed = () => {
    const collapsed = !sidebarState.collapsed
    setSidebarState((prev) => ({ ...prev, collapsed }))
    clearTimeout(collapseSaveTimer.current)
    collapseSaveTimer.current = setTimeout(() => {
      API.post('/account/uipreferences', { sidebarCollapsed: collapsed })
        .then((res) => {
          if (res.status === 200) {
            setUser((prev) => ({ ...prev, uiPreferences: res.data }))
          }
        })
        .catch(() => {})
    }, 1000)
  }
  const [searchOpen, setSearchOpen] = useState(false)
  const [existingRecord, setExistingRecord] = useState(null) // EPCR
  const [notAuthorised, setNotAuthorised] = useState(false)
  const [notAuthorisedToast, setNotAuthorisedToast] = useState(false)
  const [siteMode, setSiteMode] = useState('normal')
  const [epcrPracticeMode, setEpcrPracticeMode] = useState(false)
  const [selectedControlRoom, setSelectedControlRoom] = useState(null)
  const [selectedTreatmentLocation, setSelectedTreatmentLocation] =
    useState(null)
  const [treatmentLocationWorkstationMode, setTreatmentLocationWorkstationMode] =
    useState(false)
  const mainFrameContainerRef = useRef(null)
  const { setToast } = useContext(GlobalContext)

  const location = useLocation()
  useEffect(() => {
    setNotAuthorised(false)
    setNotAuthorisedToast(false)
  }, [location])

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  useEffect(() => {
    if (orgSettings?.setupComplete === false && user.security.isSiteAdmin) {
      setSiteMode('setup')
    }
  }, [orgSettings?.setupComplete, user?.security?.isSiteAdmin])

  useEffect(() => {
    if (!['eventControl', 'treatmentLocation'].includes(siteMode)) {
      return undefined
    }

    const keepSessionAlive = () => {
      API.get('/auth/ping', { skipGlobalErrorHandler: true }).catch(() => {})
    }

    keepSessionAlive()

    const interval = setInterval(keepSessionAlive, 60 * 1000)

    return () => clearInterval(interval)
  }, [siteMode])

  useEffect(() => {
    if (siteMode !== 'treatmentLocation' || !selectedTreatmentLocation) return

    const expectedPath = treatmentLocationWorkstationMode
      ? `/treatmentworkstations/${selectedTreatmentLocation}`
      : `/treatmentlocations/${selectedTreatmentLocation}`

    if (location.pathname === expectedPath) return

    setSiteMode('normal')
    setSelectedTreatmentLocation(null)
    setTreatmentLocationWorkstationMode(false)
  }, [
    location.pathname,
    selectedTreatmentLocation,
    siteMode,
    treatmentLocationWorkstationMode,
  ])

  useEffect(() => {
    const routeBackedSiteModes = {
      PTSControl: '/pts/control',
      PTSCrew: '/pts',
    }
    const expectedPath = routeBackedSiteModes[siteMode]

    if (!expectedPath || location.pathname === expectedPath) return

    setSiteMode('normal')
  }, [location.pathname, siteMode])

  useEffect(() => {
    setGlobalApiErrorHandler(({ response }) => {
      if (
        response?.status === 401 &&
        response?.data?.errorCode === 'notAuthorised'
      ) {
        setNotAuthorised(true)
        return true
      }

      // This handler replaces the App-level one while the frame is mounted,
      // so the "account disabled mid-session" case has to be handled here
      // too: the server has already destroyed the session.
      if (
        response?.status === 401 &&
        response?.data?.errorCode === 'loginDisabled'
      ) {
        setToast?.({
          severity: 'warning',
          message:
            'Login has been disabled for your account. Please contact your administrator.',
        })
        setUser?.({})
        return true
      }

      return false
    })

    return () => setGlobalApiErrorHandler(null)
  }, [setToast, setUser])

  const navigate = useNavigate()
  // E-learning courses run without the app's navigation and logo: CPD UK
  // guidance keeps branding off content screens. The course pages supply
  // their own plain frame (training/components/FocusLayout).
  const courseMode = location.pathname.startsWith('/training/course/')
  const hospitalPortalPreviewMode = location.pathname.startsWith(
    '/pts/hospital-portal-preview/',
  )
  // The wallboard display is a full-screen TV page — no sidebar, header or
  // banners (the /wallboard-settings editor keeps the normal frame)
  const wallboardMode = location.pathname === '/wallboard'
  const returnToTreatmentLocation = selectedTreatmentLocation
    ? () => {
        setSiteMode('treatmentLocation')
        navigate(
          treatmentLocationWorkstationMode
            ? `/treatmentworkstations/${selectedTreatmentLocation}`
            : `/treatmentlocations/${selectedTreatmentLocation}`,
        )
      }
    : null

  return (
    <SiteModeContext.Provider
      value={{
        siteMode,
        setSiteMode,
        sidebarState,
        setSidebarState,
        searchOpen,
        setSearchOpen,
        setExistingRecord,
        epcrPracticeMode,
        setEpcrPracticeMode,
        mainFrameContainerRef,
        selectedControlRoom,
        setSelectedControlRoom,
        selectedTreatmentLocation,
        setSelectedTreatmentLocation,
        treatmentLocationWorkstationMode,
        setTreatmentLocationWorkstationMode,
      }}
    >
      {hospitalPortalPreviewMode || courseMode || wallboardMode ? (
        <Box minHeight="100vh" width="100%">
          <Routes>
            <Route path="/*" element={<SiteRoutes />} />
          </Routes>
        </Box>
      ) : siteMode === 'setup' ? (
        <TenantOnboarding />
      ) : siteMode === 'ePCR' ? (
        <EPCR
          existingRecord={existingRecord}
          setExistingRecord={setExistingRecord}
          onExit={returnToTreatmentLocation}
        />
      ) : siteMode === 'eventControl' ? (
        <EventControlPage selectedControlRoom={selectedControlRoom} />
      ) : siteMode === 'treatmentLocation' ? (
        <TreatmentCentre
          selectedLocation={selectedTreatmentLocation}
          workstationMode={treatmentLocationWorkstationMode}
          onExit={() => {
            setSiteMode('normal')
            setSelectedTreatmentLocation(null)
            setTreatmentLocationWorkstationMode(false)
            navigate('/commandcontrol/treatmentlocations')
          }}
        />
      ) : siteMode === 'PTSControl' ? (
        <PTSUpgrade
          module={<PTSControllerOverview />}
          onGoBack={() => {
            setSiteMode('normal')
            navigate('/')
          }}
        />
      ) : siteMode === 'PTSCrew' ? (
        <PTSUpgrade
          module={<PTSCrewView />}
          onGoBack={() => {
            setSiteMode('normal')
            navigate('/')
          }}
        />
      ) : (
        <NavCountsProvider>
        <Box display="flex" height="100vh" overflow="hidden">
          <RecentPagesTracker userId={user?._id} />
          <Sidebar />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              height: '100%',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <Stack>
              <Header
                setSidebarState={setSidebarState}
                toggleSidebarCollapsed={toggleSidebarCollapsed}
              />
              <FavouritesBar />
              <PortalRequestsBanner />
              <ConnectionStatusBanner />
            </Stack>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 0,
                overflow: 'hidden',
                backgroundColor: 'anBackground.main',
              }}
            >
              <BillingWarning />
              <AgreementReminder />
              {notAuthorised ? (
                <NotAuthorised />
              ) : (
                <Box
                  flex={1}
                  sx={{
                    // backgroundColor: 'black',
                    // backgroundColor: '#EEEEEE',
                    paddingY: 1,
                    paddingX: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarGutter: 'stable',
                    gap: 2,
                  }}
                  ref={mainFrameContainerRef}
                >
                  <Routes>
                    <Route path="/*" element={<SiteRoutes />} />
                    <Route path="/docs/updates" element={<UpdatesHub />} />
                    <Route path="/docs/*" element={<PublicDocs />}>
                      {docRoutes.map((route, index) => (
                        <Route
                          key={index}
                          index={route.index}
                          path={route.path}
                          element={route.element}
                        />
                      ))}
                    </Route>
                  </Routes>
                  <Box
                    sx={{
                      fontFamily: 'moonlander',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#2A3948',
                      paddingY: 2,
                      // pushes the footer to the bottom without spreading
                      // pages that render more than one root element
                      marginTop: 'auto',
                    }}
                  >
                    <Box display="flex" gap={0}>
                      AmbuNet
                      <Typography fontFamily="arial" fontSize="0.75rem">
                        &reg;
                      </Typography>
                    </Box>
                    <Box component="img" width={30} src={AmbuNetLogo} />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        </NavCountsProvider>
      )}
      {/* Never idle-logout a wall-mounted display */}
      {!wallboardMode && <UserSessionIdleTimer siteMode={siteMode} />}
    </SiteModeContext.Provider>
  )
}

export default MainFrame
